import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@parsina/framework';
import { C } from '../../constants/theme';
import useResponsive from '../../hooks/useResponsive';
import MobileSheet from '../../mobile/MobileSheet';

/**
 * One task, two anatomies: a bottom sheet on the phone, a centred dialog on a
 * workstation.
 *
 * Reception, «افزودن بیمار», «افراد» and «کلینیک من» all reach for the same
 * modals from both surfaces, so a screen's overlay used to arrive on a 360px
 * phone as a 430px card floating in the middle of the viewport — cut off at
 * both edges, its submit button under the software keyboard, and dismissed by
 * the platform back button navigating the route out from under it. That is
 * what `MobileSheet` exists to fix (docs/MOBILE-REDESIGN.md §4.2), and the fix
 * only ever reached the overlays that happened to be mobile-only.
 *
 * So the fork lives HERE, once, rather than in each modal: below 768px the
 * body renders inside `MobileSheet` — docked to the bottom edge, full width,
 * `85dvh` minus the measured keyboard inset, portaled to `<body>`, focus
 * trapped, page locked, owning a history entry — and above it the screen keeps
 * the desktop shell it already had.
 *
 * `SettleHost` is the same arrangement one layer up (drawer vs sheet); this is
 * its equivalent for everything small enough that the body itself needs no
 * fork.
 *
 * **Two desktop shells.** Pass `desktop` and the screen's own centred markup is
 * used verbatim — that is for the hand-rolled overlays, whose chrome is not
 * this component's to redesign. Omit it and you get the kit `Dialog`, which is
 * what the four modals already built on `Dialog` want.
 *
 * `footer` is the commit row. On mobile it docks in the sheet's footer slot and
 * is padded for the keyboard; on the kit dialog it simply follows the body.
 * Anything hosting its own buttons inside `children` leaves it unset.
 */
export default function AdaptiveModal({
  open = true,
  onClose,
  /** The sheet's header and the dialog's `DialogTitle`. */
  title,
  /** One muted line under the title — a count, a phone number, a patient. */
  subtitle,
  /** The commit row: the sheet's docked footer, the dialog's last block. */
  footer,
  children,
  /** The desktop anatomy, already built. Omit for the kit `Dialog`. */
  desktop,
  /** Kit-`Dialog` width, when `desktop` is omitted. */
  size = 'md',
  /** Fraction of the viewport the sheet may take. */
  snap = 0.92,
  /** Sheet-side gutter around `children`. Off for a body that is a full-bleed list. */
  sheetPadded = true,
  fullHeight = true,
  'data-testid': testId,
}) {
  const { isMobile } = useResponsive();

  if (!open) return null;

  if (isMobile) {
    return (
      <MobileSheet
        open
        onClose={onClose}
        title={title}
        footer={footer}
        snap={snap}
        fullHeight={fullHeight}
        data-testid={testId}
      >
        {subtitle ? (
          <div style={{ padding: '0.75rem 1rem 0', fontSize: '0.75rem', color: C.textMuted, lineHeight: 1.9 }}>
            {subtitle}
          </div>
        ) : null}
        {sheetPadded
          ? <div style={{ padding: '0.875rem 1rem', display: 'grid', gap: '0.875rem' }}>{children}</div>
          : children}
      </MobileSheet>
    );
  }

  if (desktop) return desktop;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent size={size} data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {subtitle ? <span style={{ fontSize: 12, color: C.textMuted }}>{subtitle}</span> : null}
        </DialogHeader>
        {children}
        {footer}
      </DialogContent>
    </Dialog>
  );
}
